import React from 'react';
import PropTypes from 'prop-types';

import { useSelector, useDispatch } from 'react-redux';
import Select from 'react-select';

import Checkbox from '../../ui/Checkbox';
import FormControl from '../../ui/FormControl';
import InputGroup from '../../ui/InputGroup';
import Radio from '../../ui/Radio';
import { focusGlowStyle } from '../../ui/sharedStyles';

import { setSelectedFeatureScoreType } from '../../../actions/DeepSEAActions';
import { ANALYSES_META_INFO } from '../../../settings';
import Tooltip from '../../ui/Tooltip';
import Legend from '../../Legend';

const defaultScoreRange = [-3, 0, 3];
const FULL_VIEW = 'full';
const COMPACT_VIEW = 'compact';

// Pure function to get documentation URL based on model name
const getDocumentationUrl = (modelName, isExpecto = false) => {
  // Check for ExPecto special case
  if (modelName?.toLowerCase() === 'beluga' && isExpecto) {
    return ANALYSES_META_INFO.expecto?.helpUrl;
  }

  // Standard lookup by model name
  const modelKey = modelName?.toLowerCase();
  return ANALYSES_META_INFO[modelKey]?.helpUrl;
};

// Pure helper function to determine model from pathname
const getModelFromPathname = pathname => {
  if (!pathname) return null;
  if (pathname.includes('/asdbrowser')) return 'asdbrowser';
  if (pathname.includes('/sei')) return 'sei';
  if (pathname.includes('/beluga')) return 'beluga';
  if (pathname.includes('/seqweaver')) return 'seqweaver';
  return null;
};

export const FeatureHeatmapHeader = ({
  color,
  headerText,
  reverseOrder,
  featureScoreOptions,
  heatmapViewType,
  heatmapLabels,
  setReverseOrder,
  setSearchText,
  setHeatmapViewType,
}) => {
  const {
    selectedFeatureScoreType,
    isFetchingFeaturePredictions,
  } = useSelector(state => state.deepsea);
  const job = useSelector(state => state.deepsea.selectedJob);
  const dispatch = useDispatch();
  const scoreRange =
    (selectedFeatureScoreType && selectedFeatureScoreType.range) ||
    defaultScoreRange;

  const { title, legendText, placeHolderText } = heatmapLabels;

  return (
    <div>
      <h4>{title}</h4>
      <div style={{ width: 200, float: 'left' }}>
        <Select
          isSearchable={false}
          defaultValue={featureScoreOptions[0]}
          options={featureScoreOptions}
          value={selectedFeatureScoreType}
          onChange={selectedOption => {
            dispatch(setSelectedFeatureScoreType(selectedOption));
          }}
          styles={{
            control: (provided, state) => ({
              ...focusGlowStyle.control(provided, state),
              zIndex: 10,
            }),
          }}
        />
      </div>
      <InputGroup
        style={{
          float: 'left',
          height: 38,
          width: 300,
          marginLeft: 20,
        }}
      >
        <FormControl
          type="text"
          placeholder={placeHolderText}
          style={{ height: 38 }}
          onChange={text => setSearchText(text.target.value)}
        />
        <InputGroup.Addon>
          <i className="fa fa-search" />
        </InputGroup.Addon>
      </InputGroup>
      <div style={{ float: 'left', paddingBottom: 10, paddingLeft: 10 }}>
        {headerText}
        <Checkbox
          style={{ marginTop: 0, marginLeft: 10 }}
          key="heatmap_sort"
          checked={reverseOrder}
          onChange={() => {
            setReverseOrder(!reverseOrder);
          }}
        >
          Reverse Sort
        </Checkbox>
      </div>
      <div style={{ float: 'left', paddingBottom: 20, paddingLeft: 10 }}>
        <Radio
          value={FULL_VIEW}
          checked={heatmapViewType === FULL_VIEW}
          onChange={target => setHeatmapViewType(target.currentTarget.value)}
          style={{ marginTop: 0 }}
        >
          <strong>Full View</strong>
        </Radio>
        <Radio
          value={COMPACT_VIEW}
          checked={heatmapViewType === COMPACT_VIEW}
          onChange={target => setHeatmapViewType(target.currentTarget.value)}
        >
          <strong>Compact View</strong>
        </Radio>
      </div>
      {!isFetchingFeaturePredictions && (
        <div style={{ float: 'right', textAlign: 'center', marginTop: -15 }}>
          <div>
            {legendText}{' '}
            <Tooltip
              text="Score type documentation"
              url={`${getDocumentationUrl(
                job?.ds_model ||
                  (typeof window !== 'undefined' && window.location
                    ? getModelFromPathname(window.location.pathname)
                    : null),
                job?.expecto || false,
              )}#output`}
            />
          </div>
          <Legend width={160} height={25} color={color} range={scoreRange} />
        </div>
      )}
    </div>
  );
};

FeatureHeatmapHeader.propTypes = {
  color: PropTypes.func.isRequired,
  headerText: PropTypes.element,
  reverseOrder: PropTypes.bool,
  featureScoreOptions: PropTypes.arrayOf(PropTypes.object),
  heatmapViewType: PropTypes.string,
  heatmapLabels: PropTypes.shape({
    title: PropTypes.string,
    legendText: PropTypes.string,
    placeHolderText: PropTypes.string,
    featureTabTitle: PropTypes.string,
  }).isRequired,
  setReverseOrder: PropTypes.func,
  setSearchText: PropTypes.func,
  setHeatmapViewType: PropTypes.func,
};

FeatureHeatmapHeader.defaultProps = {
  headerText: null,
  reverseOrder: false,
  featureScoreOptions: [],
  heatmapViewType: 'full',
  setReverseOrder: null,
  setSearchText: null,
  setHeatmapViewType: null,
};

export default FeatureHeatmapHeader;
